import type { CombinedError } from '@urql/core';
import type { DocumentNode } from 'graphql';
import { useSubscription, type TypedDocumentNode } from 'urql';

export interface UseTypedSubscriptionResult<TData> {
  data: TData | undefined;
  loading: boolean;
  error: CombinedError | undefined;
}

/**
 * Typed wrapper around urql's useSubscription.
 * Returns a simple { data, loading, error } object.
 * `loading` is mapped from urql's `fetching` to keep the external API stable.
 */
export function useTypedSubscription<
  TData,
  TVars extends Record<string, unknown> = Record<string, never>,
>(
  document: DocumentNode | TypedDocumentNode<TData, TVars>,
  variables?: TVars,
): UseTypedSubscriptionResult<TData> {
  const [{ data, fetching, error }] = useSubscription<TData, TData, TVars>({
    query: document,
    variables: variables as TVars,
  });

  return { data: data ?? undefined, loading: fetching, error };
}

export interface SubscriptionHandlerOptions<TData> {
  /** Called when new subscription data arrives */
  onData?: (data: TData) => void;
  /** Called on subscription error */
  onError?: (error: CombinedError) => void;
}

/**
 * Creates a typed handler pair for subscription events.
 * Useful for updating cache or showing toast notifications.
 */
export function createSubscriptionHandler<TData>(options: SubscriptionHandlerOptions<TData>) {
  return {
    onData: (data: TData) => {
      if (options.onData) {
        options.onData(data);
      }
    },
    onError: (error: CombinedError) => {
      if (options.onError) {
        options.onError(error);
      }
    },
  };
}
